import { ButtonHTMLAttributes, ReactNode } from "react";
import { ArrowSendIcon } from "../../icons/ArrowSendIcon";
import { cn } from "../../utls/utils";
import ThemedSpinner from "../spinner";

interface SubmitButtonProps {
  isDisabled?: boolean;
  color?: "light" | "default" | "terracotta" | "beige" | "brown" | "cream";
  children?: ReactNode;
  iconOnly?: boolean;
  size?: "sm" | "md" | "lg";
  onSubmit?: (e: React.FormEvent) => void;
  icon?: React.ReactNode;
  hideArrow?: boolean;
  isLoading?: boolean;
}

export function SubmitButton({
  isDisabled,
  color = "terracotta",
  children,
  iconOnly = false,
  size = "md",
  className,
  onSubmit,
  icon,
  hideArrow = false,
  isLoading = false,
  ...props
}: SubmitButtonProps & ButtonHTMLAttributes<HTMLButtonElement>) {
  // Color variants
  const colorStyles = {
    // Harper brand colors
    terracotta:
      "border-terracotta-500 bg-terracotta-500 hover:bg-terracotta-600 focus:bg-terracotta-600",
    beige:
      "border-beige-500 bg-beige-500 hover:bg-beige-600 focus:bg-beige-600 text-brown-800",
    brown:
      "border-brown-500 bg-brown-500 hover:bg-brown-600 focus:bg-brown-600",
    cream:
      "border-cream-500 bg-cream-500 hover:bg-cream-600 focus:bg-cream-600 text-brown-800",

    // Legacy color variants
    teal: "border-teal-500 bg-teal-500 hover:bg-teal-600 focus:bg-teal-600",
    emerald:
      "border-emerald-500 bg-emerald-500 hover:bg-emerald-600 focus:bg-emerald-600",
    light:
      "border-lightGreen-400 bg-lightGreen-400 hover:bg-lightGreen-500 focus:bg-lightGreen-500",
    purple:
      "border-purple-400 bg-purple-400 hover:bg-purple-500 focus:bg-purple-500",
    default:
      "border-terracotta-500 bg-terracotta-500 hover:bg-terracotta-600 focus:bg-terracotta-600",
  };

  // Size variants
  const sizeStyles = {
    sm: iconOnly ? "w-6 h-6" : "py-1 px-3 text-xs",
    md: iconOnly ? "w-8 h-8" : "py-2 px-4 text-sm",
    lg: iconOnly ? "w-10 h-10" : "py-2.5 px-5 text-base",
  };

  // Icon color
  const iconColor =
    color === "default"
      ? "text-gray-700"
      : color === "beige" || color === "cream"
      ? "text-brown-800"
      : "text-white";

  // Text color
  const textColor =
    color === "default"
      ? "text-gray-700"
      : color === "beige" || color === "cream"
      ? "text-brown-800"
      : "text-white";

  const handleFormSubmit = (e: React.FormEvent) => {
    if (onSubmit) {
      onSubmit(e);
    }
  };

  return (
    <button
      disabled={isDisabled}
      onSubmit={handleFormSubmit}
      type="submit"
      className={cn(
        "rounded-lg flex items-center justify-center transition-colors duration-150 font-medium gap-2",
        colorStyles[color],
        sizeStyles[size],
        isDisabled && "opacity-50 cursor-not-allowed",
        className
      )}
      {...props}
    >
      {isLoading ? (
        <ThemedSpinner size="sm" />
      ) : iconOnly ? (
        icon || <ArrowSendIcon className={iconColor} />
      ) : (
        <>
          {!hideArrow &&
            (icon || <ArrowSendIcon className={cn(iconColor, "w-3 h-3")} />)}
          {children && <span className={textColor}>{children}</span>}
        </>
      )}
    </button>
  );
}
